// client/src/utils/pdfExport.js
// Utilities for exporting filled PDF forms
import html2canvas from 'html2canvas';
import { exportPdfForm } from './pdfForms';
import { getPdfPageSize, loadPdfDocument } from './pdfjs';
import { formatDate } from './date';
import {
    applyFieldTextLayout,
    isFieldValueEmpty,
    normalizeValuesJson,
    resolveFieldValue,
} from './formFieldLayout';

const DEFAULT_EXPORT_SCALE = 3;

async function waitForLayoutPass() {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        return;
    }
    await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
}

function formatExportDate(date = new Date()) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}:${month}:${year}`;
}

function buildFormFilename(formName, jobNumber, date = new Date()) {
    const safeForm = formName || 'form';
    const safeJob = jobNumber || 'J-00000';
    return `${safeForm}_${safeJob}_${formatExportDate(date)}.pdf`;
}

function hasValidRect(field) {
    const xNorm = Number(field?.xNorm);
    const yNorm = Number(field?.yNorm);
    const wNorm = Number(field?.wNorm);
    const hNorm = Number(field?.hNorm);
    if (!Number.isFinite(xNorm) || !Number.isFinite(yNorm) || !Number.isFinite(wNorm) || !Number.isFinite(hNorm)) {
        return false;
    }
    return wNorm > 0 && hNorm > 0;
}

function normalizeRectStyle(value) {
    const fallback = {
        strokeColor: '#ffffff',
        fillColor: '#ffffff',
        fillEnabled: true,
        strokeWidth: 1,
        opacity: 1,
    };
    if (!value) {
        return fallback;
    }
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return { ...fallback, ...parsed };
            }
        } catch {
            return fallback;
        }
    }
    if (typeof value === 'object') {
        return { ...fallback, ...value };
    }
    return fallback;
}

async function renderOverlayPage({ pdfDoc, pageIndex, fields, values, exportScale }) {
    const size = await getPdfPageSize(pdfDoc, pageIndex);
    const width = size.width * exportScale;
    const height = size.height * exportScale;
    const container = document.createElement('div');
    container.className = 'pdf-export-layer';
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.pointerEvents = 'none';
    container.style.background = 'transparent';
    container.style.fontFamily = 'NotoSansHebrew, Assistant, sans-serif';
    container.style.fontSize = '12px';
    container.style.direction = 'rtl';
    document.body.appendChild(container);

    const textLayoutJobs = [];

    const pageFields = fields
        .filter((field) => field.pageIndex === pageIndex)
        .slice()
        .sort((a, b) => (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0));

    pageFields.forEach((field) => {
        if (!hasValidRect(field)) {
            return;
        }
        const value = resolveFieldValue(field, values);
        if (field.type === 'image') {
            if (!field.assetDataUrl) {
                return;
            }
        } else if (field.type !== 'rectangle' && isFieldValueEmpty(field, value)) {
            return;
        }

        const rect = {
            x: field.xNorm * width,
            y: field.yNorm * height,
            width: field.wNorm * width,
            height: field.hNorm * height,
        };
        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.left = `${rect.x}px`;
        wrapper.style.top = `${rect.y}px`;
        wrapper.style.width = `${rect.width}px`;
        wrapper.style.height = `${rect.height}px`;
        wrapper.style.boxSizing = 'border-box';

        if (field.type === 'rectangle') {
            const style = normalizeRectStyle(field.styleJson);
            const strokeWidth = Math.max(0, Number(style.strokeWidth) || 0);
            wrapper.style.background = style.fillEnabled ? style.fillColor || 'transparent' : 'transparent';
            wrapper.style.border = strokeWidth > 0 ? `${strokeWidth}px solid ${style.strokeColor || '#2563eb'}` : 'none';
            wrapper.style.opacity = Number.isFinite(style.opacity) ? style.opacity : 1;
            wrapper.style.padding = '0';
            container.appendChild(wrapper);
            return;
        }

        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.padding = '2px 4px';

        if (field.type === 'image') {
            const img = document.createElement('img');
            img.src = field.assetDataUrl;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'fill';
            wrapper.style.padding = '0';
            wrapper.appendChild(img);
            container.appendChild(wrapper);
            return;
        }

        if (field.type === 'signature' && typeof value === 'string') {
            wrapper.style.padding = '0';
            const img = document.createElement('img');
            img.src = value;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            img.style.objectPosition = 'center';
            wrapper.appendChild(img);
        } else {
            const textSpan = document.createElement('span');
            wrapper.appendChild(textSpan);
            textLayoutJobs.push({
                field,
                value,
                wrapper,
                textNode: textSpan,
            });
        }

        container.appendChild(wrapper);
    });

    if (document.fonts && document.fonts.ready) {
        try {
            await document.fonts.ready;
        } catch {
            // ignore font loading errors
        }
    }
    await waitForLayoutPass();
    textLayoutJobs.forEach(({ field, value, wrapper, textNode }) => {
        applyFieldTextLayout({
            wrapper,
            textNode,
            field,
            value,
            baseFontSize: (Number(field.fontSize) || 12) * exportScale,
            fontScale: exportScale,
            color: '#111827',
            formatDateFn: formatDate,
        });
    });
    await waitForLayoutPass();
    const canvas = await html2canvas(container, {
        scale: 1,
        backgroundColor: null,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
    });
    container.remove();
    return canvas.toDataURL('image/png');
}

async function exportPdfFormWithOverlays({
    formId,
    template,
    fields = [],
    values = {},
    exportScale = DEFAULT_EXPORT_SCALE,
}) {
    if (!template?.fileUrl) {
        throw new Error('Template file missing');
    }
    const pdfDoc = await loadPdfDocument(template.fileUrl);
    const pageCount = pdfDoc.numPages || template.pageCount || 1;
    const normalizedValues = normalizeValuesJson(values);
    const safeFields = fields.filter((field) => field.type !== 'sectionBreak');
    const overlays = [];

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        const pngDataUrl = await renderOverlayPage({
            pdfDoc,
            pageIndex,
            fields: safeFields,
            values: normalizedValues,
            exportScale,
        });
        overlays.push({ pageIndex, pngDataUrl });
    }

    const { blob } = await exportPdfForm(formId, overlays);
    return blob;
}

export {
    exportPdfFormWithOverlays,
    buildFormFilename,
    formatExportDate,
};
